import { CheckCircle } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogProps,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { FC } from "react";

export interface TransitionRoutesPhaseModalProps
  extends Omit<
    DialogProps,
    "onClose" | "open" | "style" | "inputValue" | "handler"
  > {
  description?: string;
  onClose: () => void;
  open?: boolean;
  status?: PhaseStatus;
  transitionPhase?: TransitionPhase;
  isCustom?: boolean;
}

export type TransitionPhase = "build" | "live" | "revert";
export type PhaseStatus = "loading" | "success";

type PhaseMap = {
  [phase in TransitionPhase]: {
    [status in PhaseStatus]: {
      title: string;
      body: string;
    };
  };
};

const TransitionRoutesPhaseModal: FC<TransitionRoutesPhaseModalProps> = (
  props,
) => {
  const { sx, onClose, open, status, transitionPhase, isCustom, ...rest } =
    props;

  const getTextContent = (phase?: TransitionPhase, status?: PhaseStatus) => {
    if (!phase || !status)
      return {
        title: props.title,
        body: props.description,
      };

    const phaseMap: PhaseMap = {
      build: {
        loading: {
          title: "Creating Routes",
          body: "Routes for the morning shift are being created and optimized ...",
        },
        success: {
          title: "Routes Created!",
          body: "Routes for the morning shift are ready to be sent to drivers.",
        },
      },
      live: {
        loading: {
          title: "Sending to Drivers",
          body: "Routes are being sent to drivers.",
        },
        success: {
          title: "Routes Sent!",
          body: "Drivers have been notified about their routes.",
        },
      },
      revert: {
        loading: {
          title: "Reverting to Planning",
          body: "Route is reverting to the default statuses in the planning phase.",
        },
        success: {
          title: "Reverted to Planning",
          body: "All changes in the routes have been reset to the default daily statuses.",
        },
      },
    };

    return phaseMap[phase]?.[status];
  };

  if ((!status || !transitionPhase) && !isCustom) return;

  const { title, body } = getTextContent(
    transitionPhase,
    status as PhaseStatus,
  );

  return (
    <Dialog onClose={onClose} {...rest} sx={sx || {}} open={open || false}>
      <DialogContent
        sx={{
          padding: "56px",
          display: "flex",
          flexDirection: "column",
          gap: "40px",
          alignItems: "center",
        }}
      >
        {status === "loading" ? (
          <>
            <CircularProgress />
            <Box
              sx={{
                marginTop: "40px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "16px",
                color: grey[100],
                textAlign: "center",
              }}
            >
              <Typography variant="titleLarge">{title}</Typography>
              <Typography variant="bodyMedium">{body}</Typography>
            </Box>
          </>
        ) : (
          <>
            <CheckCircle
              sx={{ width: "60px", height: "60px", color: "#8ACEFF" }}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: "16px",
                color: grey[100],
                textAlign: "center",
              }}
            >
              <Typography variant="titleLarge">{title}</Typography>
              <Typography variant="bodyMedium">{body}</Typography>
            </Box>
            <Button variant="contained" onClick={onClose}>
              Got it
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TransitionRoutesPhaseModal;
